import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, SimpleSelect } from "@/components/common";
import { api } from "@/lib/api";
import { act } from "@/lib/useApi";

const EMPTY = { full_name: "", cpf: "", birth_date: "", phone: "", email: "", address: "", guardian_name: "", guardian_phone: "",
  enrollment_date: "", status: "ativo", plan_id: "", monthly_fee: 0, due_day: 10, payment_method: "pix", notes: "", belt: "Branca", grau: 0, class_ids: [], lgpd_consent: false };

const ageOf = (b) => {
  if (!b) return null;
  const d = new Date(b + "T12:00:00"), t = new Date();
  return t.getFullYear() - d.getFullYear() - (t < new Date(t.getFullYear(), d.getMonth(), d.getDate()) ? 1 : 0);
};

export function StudentForm({ open, onClose, student, plans = [], classes = [], belts = [], onSaved }) {
  const [f, setF] = useState(student ? { ...EMPTY, ...student, email: student.email || "" } : EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const minor = (ageOf(f.birth_date) ?? 99) < 18;
  const editing = !!student;

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, email: f.email || null, plan_id: f.plan_id || null, monthly_fee: Number(f.monthly_fee) || 0,
      due_day: Number(f.due_day), grau: Number(f.grau), enrollment_date: f.enrollment_date || null };
    const r = await act(() => (editing ? api.put(`/students/${student.id}`, body) : api.post("/students", body)), editing ? "Cadastro atualizado" : "Aluno matriculado");
    if (r) { onSaved?.(r); onClose(); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="student-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{editing ? "Editar aluno" : "Nova matrícula"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nome completo *" className="sm:col-span-2"><Input required value={f.full_name} onChange={set("full_name")} data-testid="sf-name" /></Field>
            <Field label="CPF *"><Input required value={f.cpf} onChange={set("cpf")} placeholder="000.000.000-00" data-testid="sf-cpf" /></Field>
            <Field label="Data de nascimento *"><Input type="date" required value={f.birth_date} onChange={set("birth_date")} data-testid="sf-birth" /></Field>
            <Field label="Telefone"><Input value={f.phone} onChange={set("phone")} data-testid="sf-phone" /></Field>
            <Field label="E-mail"><Input type="email" value={f.email} onChange={set("email")} data-testid="sf-email" /></Field>
            <Field label="Endereço" className="sm:col-span-2"><Input value={f.address} onChange={set("address")} data-testid="sf-address" /></Field>
          </div>
          {minor && (
            <div className="grid sm:grid-cols-2 gap-3 rounded-lg bg-amber-50 p-3" data-testid="sf-guardian-section">
              <div className="sm:col-span-2 text-xs font-bold uppercase text-amber-700">Menor de idade · responsável legal obrigatório</div>
              <Field label="Nome do responsável *"><Input required value={f.guardian_name} onChange={set("guardian_name")} data-testid="sf-guardian-name" /></Field>
              <Field label="Telefone do responsável *"><Input required value={f.guardian_phone} onChange={set("guardian_phone")} data-testid="sf-guardian-phone" /></Field>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-3">
            {!editing && <>
              <Field label="Data de matrícula"><Input type="date" value={f.enrollment_date} onChange={set("enrollment_date")} data-testid="sf-enroll" /></Field>
              <Field label="Status"><SimpleSelect value={f.status} onChange={set("status")} testid="sf-status"
                options={["ativo", "experimental", "inativo"].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))} /></Field>
              <Field label="Plano"><SimpleSelect value={f.plan_id} onChange={set("plan_id")} testid="sf-plan" options={plans.map((p) => ({ value: p.id, label: p.name }))} /></Field>
              <Field label="Faixa inicial"><SimpleSelect value={f.belt} onChange={set("belt")} testid="sf-belt" options={belts.map((b) => ({ value: b, label: b }))} /></Field>
              <Field label="Grau"><Input type="number" min={0} max={6} value={f.grau} onChange={set("grau")} data-testid="sf-grau" /></Field>
            </>}
            <Field label="Mensalidade (R$, 0 = do plano)"><Input type="number" step="0.01" min={0} value={f.monthly_fee} onChange={set("monthly_fee")} data-testid="sf-fee" /></Field>
            <Field label="Dia de vencimento"><Input type="number" min={1} max={28} value={f.due_day} onChange={set("due_day")} data-testid="sf-due-day" /></Field>
            <Field label="Forma de pagamento"><SimpleSelect value={f.payment_method} onChange={set("payment_method")} testid="sf-method"
              options={[["pix", "Pix"], ["dinheiro", "Dinheiro"], ["cartao_recorrente", "Cartão recorrente (Stripe)"], ["cartao_maquina", "Cartão maquininha"]].map(([value, label]) => ({ value, label }))} /></Field>
          </div>
          <Field label="Turmas">
            <div className="flex flex-wrap gap-2" data-testid="sf-classes">
              {classes.map((c) => {
                const on = f.class_ids.includes(c.id);
                return <button type="button" key={c.id} onClick={() => setF({ ...f, class_ids: on ? f.class_ids.filter((x) => x !== c.id) : [...f.class_ids, c.id] })}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ring-1 transition-colors ${on ? "bg-red-600 text-white ring-red-600" : "ring-slate-200 hover:ring-red-300"}`}
                  data-testid={`sf-class-${c.id}`}>{c.name}</button>;
              })}
            </div>
          </Field>
          <Field label="Observações administrativas"><Textarea value={f.notes} onChange={set("notes")} data-testid="sf-notes" /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.lgpd_consent} onChange={(e) => setF({ ...f, lgpd_consent: e.target.checked })} data-testid="sf-lgpd" />
            Consentimento LGPD para tratamento dos dados pessoais coletado</label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="sf-submit">{editing ? "Salvar" : "Matricular"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
